import { useMutation } from '@apollo/client'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useRef } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  Icon,
  IconType,
  NotificationType,
  TextField,
} from '../../../components'
import { UPDATE_CAMPAIGN, MutationUpdateCampaignResponse } from '../../../gql'
import { Campaign, MutationUpdateCampaignArgs } from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'

type EditCampaignTitleFormData = {
  title: string
}

interface IProps {
  campaign: Campaign
  isOpen: boolean
  setIsOpen: (event: any) => void
  onCampaignUpdated?: () => void
}

export const UpdateCampaignTitleModal = ({
  campaign,
  isOpen,
  setIsOpen,
  onCampaignUpdated,
}: IProps) => {
  const { addNotification } = useNotification()
  const cancelButtonRef = useRef(null)

  const [updateCampaignTitle, { loading }] = useMutation<
    MutationUpdateCampaignResponse,
    MutationUpdateCampaignArgs
  >(UPDATE_CAMPAIGN)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditCampaignTitleFormData>({
    defaultValues: {
      title: campaign?.title ?? '',
    },
  })

  const onSubmit = async (data: EditCampaignTitleFormData) => {
    try {
      await updateCampaignTitle({
        variables: {
          id: Number(campaign.id),
          input: {
            title: data.title,
          },
        },
      })

      reset()
      closeModal()
      addNotification(
        `Campaign title updated successfully`,
        NotificationType.success,
      )
      onCampaignUpdated?.()
    } catch (error) {
      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
    }
  }

  const closeModal = () => {
    setIsOpen(false)
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-10'
        initialFocus={cancelButtonRef}
        onClose={setIsOpen}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed z-10 inset-0 overflow-y-auto'>
          <div className='flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='relative bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-xl sm:w-full'>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className='bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4'>
                    <div className='sm:flex sm:items-start'>
                      <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10'>
                        <div aria-hidden='true'>
                          <Icon
                            type={IconType.edit}
                            className='h-5 w-5 text-blue-600'
                            aria-hidden='true'
                          />
                        </div>
                      </div>
                      <div className='mt-3 sm:mt-0 sm:ml-4 sm:text-left w-full'>
                        <Dialog.Title
                          as='h3'
                          className='text-lg font-semibold font-sans leading-6 text-gray-900'
                        >
                          Edit Campaign Title
                        </Dialog.Title>
                        <div className='my-4'>
                          <p className='mt-1 text-sm font-normal leading-5 text-gray-500 font-sans'>
                            This information will not be visible to creators.
                            Creators will only see your brand and product
                            details.
                          </p>
                        </div>
                        <div className='mt-4'>
                          <TextField
                            label='Campaign title'
                            name='title'
                            placeholder={campaign?.title}
                            error={errors.title?.message}
                            {...register('title', {
                              required: `Please set a campaign title.`,
                              minLength: {
                                value: 2,
                                message: `Your campaign title must be longer than or equal to 2 characters.`,
                              },
                              maxLength: {
                                value: 100,
                                message: `Your campaign title cannot have more than 100 characters.`,
                              },
                            })}
                            autoFocus
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'>
                    <Button
                      title='Save'
                      type='submit'
                      loading={loading}
                      colour={ButtonColor.primary}
                      className='w-full sm:w-auto mx-1.5 py-3 sm:py-2'
                    />
                    <button
                      type='button'
                      className='mt-3 mx-1.5 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                      onClick={closeModal}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
